/**
 * Failure wording for "Check Email for Articles".
 *
 * WI-CHECKEMAIL-ERROR-HONESTY-001. `POST /api/articles/check-email` is gated on
 * the integration domain's health (`article_tasks.py`). When that gate refuses it
 * raises 503 **before the handler runs** — the mailbox is never contacted.
 *
 * The old handler caught every error identically and said "please verify email
 * settings", so a health-gate refusal sent the CEO to debug settings that were
 * never the problem. That is worse than an unhelpful message: it spends the
 * reader's time in the wrong system. Surfaced by
 * WI-INTEGRATION-DOMAIN-RED-REPAIR-001, where this was the only genuinely broken
 * control in the reported incident.
 *
 * 503 detection mirrors `showGenericError` in
 * `components/inbox/cockpit/useInboxCockpitState.js`, which is the canonical
 * implementation. It is duplicated rather than shared here deliberately: that one
 * also returns its message for a separate screen-reader announcement, so
 * unifying them is a refactor beyond this fix's scope. If a third site ever needs
 * this, unify all three then — two copies of a predicate is the cheap end of the
 * dual-store failure class, and three is where it starts to bite.
 */

// Kept as exports so tests assert the exact words the CEO reads, rather than
// re-typing them and drifting from the source.
export const EMAIL_SETTINGS_MESSAGE =
  'Could not check email — please verify email settings';

export const AREA_UNAVAILABLE_MESSAGE =
  'System is syncing — no email was checked. Please try again in a moment';

/**
 * Choose the message for a failed check-email attempt.
 *
 * @param {unknown} err  the thrown value (lib/api.js attaches `.status`)
 * @returns {string}     a business-facing sentence, never tech jargon
 */
export function checkEmailErrorMessage(err) {
  const status = err?.status ?? err?.response?.status;

  // Word-bounded so an unrelated number containing "503" (e.g. "1503 articles")
  // is not read as a status code. The message arm exists because some callers
  // surface only `HTTP 503: ...` text with no structured status.
  const messageSays503 =
    typeof err?.message === 'string' && /\b503\b/.test(err.message);

  return status === 503 || messageSays503
    ? AREA_UNAVAILABLE_MESSAGE
    : EMAIL_SETTINGS_MESSAGE;
}
